import { useMemo, useRef } from 'react';
import { useInfiniteQuery, InfiniteData } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { QUERY_KEYS } from '@restworld/utils-common';
import { useJobPositionsService, ListJobPositions, ListJobPositionItem } from '@restworld/data-services';
import { useAuth } from '@restworld/feature-authentication';

const DEFAULT_OFFSET = 0;

interface UseEmployerJobListingsOptions {
  limit?: number;
  searchVector?: string;
  orderBy?: string[];
  restaurantName?: string;
  status?: string;
  location?: string;
  occupationId?: string;
  showPerformanceMetrics?: boolean;
  enabled?: boolean;
}

function useEmployerJobListings(options?: UseEmployerJobListingsOptions) {
  const {
    searchVector,
    orderBy,
    restaurantName,
    status,
    location,
    occupationId,
    limit = 10,
    showPerformanceMetrics = true,
    enabled,
  } = options || {};
  const jobPositionsService = useJobPositionsService();
  const { user } = useAuth();
  const organizationId = user?.organization_id;
  const nextPageRef = useRef<number>();
  const hasNextPageRef = useRef<boolean>();

  const orderKey = useMemo(() => (orderBy ? JSON.stringify(orderBy) : ''), [orderBy]);

  const query = useInfiniteQuery({
    queryKey: [
      QUERY_KEYS.JOB_POSITIONS.LIST,
      organizationId,
      searchVector,
      orderKey,
      restaurantName,
      status,
      location,
      occupationId,
      showPerformanceMetrics,
    ],
    queryFn: ({ pageParam }) => {
      nextPageRef.current = pageParam + 1;
      return jobPositionsService.listJobPositions({
        limit,
        offset: pageParam * limit,
        organization_id: organizationId,
        search_vector: searchVector,
        order_by: orderBy,
        status,
        show_performance_metrics: showPerformanceMetrics,
        restaurant_name: restaurantName ? [restaurantName] : undefined,
        locality: location ? [location] : undefined,
        occupation_id: occupationId || undefined,
      });
    },
    getNextPageParam: (lastPage) => {
      const totalHits = lastPage.data.data.index_job_positions.hits;
      const jobPositionsLoadedSoFar = nextPageRef.current * limit;

      hasNextPageRef.current = jobPositionsLoadedSoFar < totalHits;
      return nextPageRef.current;
    },
    refetchOnWindowFocus: false,
    initialPageParam: DEFAULT_OFFSET,
    enabled,
  });

  const jobPositionsInfo = useMemo(() => extractJobPositionsInfo(query.data), [query.data]);

  return {
    query,
    jobPositionItems: jobPositionsInfo.items,
    hits: jobPositionsInfo.hits,
    hasNextPage: hasNextPageRef.current,
    loading: query.isPending,
  };
}

export default useEmployerJobListings;

type JobPositionsInfo = {
  items: ListJobPositionItem[];
  hits: number;
};

export function extractJobPositionsInfo(
  data?: InfiniteData<AxiosResponse<ListJobPositions, unknown>>
): JobPositionsInfo {
  if (!data || !data.pages) return { items: [], hits: 0 };
  const items = data?.pages.flatMap((page) => page.data.data.index_job_positions.data);
  const hits = data?.pages[0].data.data.index_job_positions.hits;

  return { items, hits };
}
